import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

/**
 * Disattivare uno sport, sapendo prima chi lo usa (D5 di docs/sport-config).
 *
 * Uno sport non si cancella: ci puntano atleti, coach, modelli, classifiche.
 * Disattivato non si propone più a nessuno, ma chi lo usa già non perde niente,
 * e riattivarlo rimette tutto com'era. La finestra lo dice con i numeri, presi
 * da `usagePath` (`/admin/sports/{id}/usage` per l'admin, `/sports/{id}/usage`
 * per il coach che l'ha creato). La stessa per entrambi.
 *
 * Props: sport, open, onOpenChange, usagePath, onConfirm (async).
 */
export function DeactivateSportDialog({ sport, open, onOpenChange, usagePath, onConfirm }) {
	const { t } = useLanguage();
	const [usage, setUsage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open || !usagePath) return undefined;
		let cancelled = false;
		setUsage(null);
		setLoading(true);
		fetchAPI(usagePath)
			.then((data) => {
				if (!cancelled) setUsage(data);
			})
			.catch(() => {
				if (!cancelled) setUsage(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, usagePath]);

	const confirm = async () => {
		setSaving(true);
		try {
			await onConfirm?.();
			onOpenChange(false);
		} finally {
			setSaving(false);
		}
	};

	const rows = usage
		? [
				["sdUsageAthletes", usage.athletes],
				["sdUsageCoaches", usage.coaches],
				["sdUsageModels", usage.models],
			]
		: [];

	return (
		<Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
			<DialogContent className="bg-card border-border max-w-md">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle>
						{t("sdDeactivateTitle").replace("{label}", sport?.label || sport?.value || "")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-3 text-sm">
					{loading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							{t("loading")}
						</div>
					) : usage ? (
						<ul className="space-y-1" data-testid="sport-usage">
							{rows.map(([key, count]) => (
								<li key={key} className="flex justify-between gap-3">
									<span className="text-muted-foreground">{t(key)}</span>
									<span className="font-medium tabular-nums">{count ?? 0}</span>
								</li>
							))}
						</ul>
					) : (
						<p className="text-muted-foreground">{t("sdUsageUnavailable")}</p>
					)}
					<p className="rounded-sm border border-border bg-secondary/40 p-2 text-muted-foreground">
						{t("sdDeactivateKeeps")}
					</p>
					<div className="flex justify-end gap-2 pt-1">
						<Button
							variant="ghost"
							className="rounded-sm"
							disabled={saving}
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							variant="destructive"
							className="rounded-sm"
							disabled={saving || loading}
							onClick={confirm}
						>
							{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
							{t("sdDeactivate")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
